import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppToken } from './useAppToken';
import { isAuthenticated } from '../auth/authentikOidc';

const API_URL = process.env.REACT_APP_API_URL || '';
const CACHE_TTL_MS = 60_000; // 60s, short enough for subscription-status changes

/**
 * useUser, fetches the current user from /api/users/me.
 *
 * State machine: idle → loading → success | error.
 * Mount-only auto-fetch; call `refetch()` to force a refresh
 * (e.g. after a successful Stripe checkout claims an entitlement).
 *
 * Returns:
 *   {
 *     user,        // backend user record or null
 *     isLoading,   // true while a fetch is in flight
 *     error,       // last unexpected error (401 is treated as logged-out, not an error)
 *     refetch,     // () => Promise<user|null>, bypasses cache
 *   }
 *
 * 401 from the backend is treated as logged-out (user=null), not an error,
 * so callers don't need to special-case auth failures in their UI.
 */
export function useUser() {
  const { getToken } = useAppToken();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchedAtRef = useRef(0);
  // Mirror of the `user` state for use inside fetchUser without making it a
  // dep, keeps fetchUser (and therefore refetch) referentially stable so
  // consumers that put refetch in their effect deps don't re-trigger every
  // time the user record updates.
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const fetchUser = useCallback(
    async ({ force = false } = {}) => {
      // Bail early if not logged in, saves a guaranteed-401 roundtrip
      if (!isAuthenticated()) {
        setUser(null);
        setIsLoading(false);
        setError(null);
        return null;
      }

      // Cache check (unless force)
      if (
        !force &&
        Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS &&
        userRef.current
      ) {
        return userRef.current;
      }

      setIsLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) {
          // Token is gone (refresh failed), treat as logged out
          setUser(null);
          setIsLoading(false);
          return null;
        }
        const res = await fetch(`${API_URL}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          // Token rejected, treat as logged out
          setUser(null);
          setIsLoading(false);
          return null;
        }
        if (!res.ok) {
          throw new Error(`/api/users/me returned ${res.status}`);
        }
        const data = await res.json();
        const next = data.user || null;
        setUser(next);
        lastFetchedAtRef.current = Date.now();
        setIsLoading(false);
        return next;
      } catch (err) {
        setError(err);
        setIsLoading(false);
        return null;
      }
    },
    [getToken]
  );

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only fetch, refetch via the returned function

  const refetch = useCallback(() => fetchUser({ force: true }), [fetchUser]);

  return useMemo(
    () => ({ user, isLoading, error, refetch }),
    [user, isLoading, error, refetch]
  );
}
